/*
 * oath-gift-wrap.js  (v6 — non-invasive, no fetch override)
 * --------------------------------------------------------------------------
 * Adds a "Gift Wrap & Personalization" product as a linked line item, and
 * removes it automatically when the main product leaves the cart.
 *
 * IMPORTANT DESIGN NOTE
 *   This version does NOT override window.fetch. Overriding fetch was what
 *   blocked Horizon's native add-to-cart. Everything here is passive:
 *     - ADD: a click listener notices the add-to-cart click, then POLLS
 *            /cart.js until the main product appears, then adds the wrap.
 *            It never calls preventDefault/stopPropagation, so the theme's
 *            own add runs completely untouched.
 *     - REMOVE: a MutationObserver on the cart drawer + passive cart-event
 *            listeners trigger reconciliation (remove orphaned wraps).
 *
 * NOTE ISOLATION (v5)
 *   The gift note is stored ONLY on the WRAP line. The note field is
 *   detached from the product form (its `name` / `form` attributes are
 *   removed) so Horizon's native add-to-cart can never send it as a
 *   property of the main product. We read the note ourselves via
 *   [data-oath-gift-note] and attach it only to the wrap.
 * --------------------------------------------------------------------------
 */
(function () {
  if (window.__oathGiftWrapInit) return;
  window.__oathGiftWrapInit = true;

  var PARENT_PROP = '_oath_gift_parent';
  var DEFAULT_NOTE_KEY = 'Gift note';
  var DEBUG = true; // set to false once everything works

  function log() {
    if (DEBUG) console.log.apply(console, ['[oath-gift-wrap]'].concat([].slice.call(arguments)));
  }

  function root() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function getBlock() {
    return document.querySelector('[data-oath-gift-block]');
  }

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  // --- NOTE ISOLATION --------------------------------------------------------
  // Strip the note field (and anything else inside the gift block) out of the
  // product form, so the theme's own add never sends it with the main product.
  function detachNoteFromForm() {
    // Also covers the theme's hidden mirror field (#giftwrap /
    // properties[Personalized note]) that an inline script fills from the
    // textarea — that field is what put the note on the main product.
    var fields = document.querySelectorAll(
      '[data-oath-gift-note], [data-oath-gift-block] input, [data-oath-gift-block] textarea, [data-oath-gift-block] select, ' +
      '#giftwrap, [name="properties[Personalized note]"]'
    );
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      if (f.hasAttribute('name')) {
        f.setAttribute('data-oath-orig-name', f.getAttribute('name'));
        f.removeAttribute('name');
      }
      if (f.hasAttribute('form')) f.removeAttribute('form');
    }
  }

  // Read everything we need at the moment of the add click.
  function readIntent() {
    var block = getBlock();
    if (!block) return null;
    var wrapVariant = block.getAttribute('data-oath-wrap-variant');
    if (!wrapVariant) return null;

    // Wrap is added ONLY when the gift box (<details>) is open.
    var noteEl = block.querySelector('[data-oath-gift-note]');
    var note = ((noteEl && noteEl.value) || '').trim();
    var enabled = !!block.open;

    // Resolve the main product's selected variant from the product form.
    var mainVariant = null;
    var formId = block.getAttribute('data-oath-product-form-id');
    var form = formId && document.getElementById(formId);
    if (form) { try { mainVariant = new FormData(form).get('id'); } catch (e) {} }
    if (!mainVariant) {
      var sel = document.querySelector('[name="id"]');
      mainVariant = sel && sel.value;
    }

    return {
      wrapVariant: Number(wrapVariant),
      noteKey: block.getAttribute('data-oath-note-key') || DEFAULT_NOTE_KEY,
      note: note,
      enabled: enabled,
      mainVariant: mainVariant ? String(mainVariant) : null
    };
  }

  // Tell Horizon to re-render the cart UI (does not modify the cart).
  function refreshUI(allowOpen) {
    getCart().then(function (cart) {
      try {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              itemCount: cart.item_count,
              source: 'product-form-component'
            }
          }
        }));
      } catch (e) {}
      try {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (e) {}
      if (allowOpen) {
        var drawer = document.querySelector('cart-drawer-component, cart-drawer');
        if (drawer) {
          try {
            if (typeof drawer.open === 'function') drawer.open();
            else drawer.open = true;
          } catch (e) {}
        }
      }
    }).catch(function () {
      try {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { itemCount: 1, source: 'product-form-component' } }
        }));
      } catch (e) {}
    });
  }

  // --- ADD -----------------------------------------------------------------
  var addingFor = {}; // guard against double-adds per main variant

  function addWrap(mainVariant, intent) {
    if (!intent.wrapVariant || !mainVariant) return Promise.resolve();
    if (addingFor[mainVariant]) return Promise.resolve();
    addingFor[mainVariant] = true;

    // The note goes ONLY here, on the wrap line.
    var props = {};
    props[PARENT_PROP] = String(mainVariant);
    if (intent.note) props[intent.noteKey] = intent.note;

    log('adding wrap', intent.wrapVariant, 'for parent', mainVariant);
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: intent.wrapVariant, quantity: 1, properties: props })
    }).then(function (res) {
      if (!res.ok) {
        return res.json().catch(function () { return {}; }).then(function (err) {
          console.warn('[oath-gift-wrap] wrap add rejected (' + res.status + '):',
            (err && (err.description || err.message)) || 'unknown',
            '| wrap variant id:', intent.wrapVariant);
        });
      }
      log('wrap added');
    }).catch(function (e) {
      log('wrap add failed', e);
    }).then(function () {
      setTimeout(function () { delete addingFor[mainVariant]; }, 800);
    });
  }

  // Safety net: right before any form submits, make sure the note field
  // is not part of it (Horizon may re-render the block on variant change).
  document.addEventListener('submit', detachNoteFromForm, true);

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var btn = t.closest('[name="add"], button[type="submit"], input[type="submit"], add-to-cart-component button, .product-form__submit, [data-add-to-cart]');
    if (!btn) return;

    // Always detach first — even when the wrap is OFF, the main product
    // must never carry the note.
    detachNoteFromForm();

    var intent = readIntent();
    if (!intent || !intent.enabled) { log('add click — wrap is OFF, skipping'); return; }
    if (!intent.mainVariant) { log('add click — could not read main variant'); return; }
    var main = intent.mainVariant;

    suspendReconcile = true;
    addWrap(main, intent); // fired before the theme's own add → main ends on top

    var tries = 0;
    (function poll() {
      tries++;
      getCart().then(function (now) {
        var mainPresent = (now.items || []).some(function (i) {
          var p = i.properties || {};
          return !p[PARENT_PROP] && String(i.variant_id || i.id) === String(main);
        });
        if (mainPresent) {
          suspendReconcile = false;
          refreshUI(true);
        } else if (tries < 16) {
          setTimeout(poll, 250); // up to ~4s
        } else {
          log('main product did not appear (add may have failed) — cleaning up');
          suspendReconcile = false;
          scheduleReconcile();
        }
      }).catch(function () {
        if (tries < 16) setTimeout(poll, 250);
        else { suspendReconcile = false; scheduleReconcile(); }
      });
    })();
  }, true);

  // --- REMOVE --------------------------------------------------------------
  var reconciling = false;
  var suspendReconcile = false;
  var reconcileTimer = null;

  function reconcile() {
    if (reconciling || suspendReconcile) return;
    reconciling = true;
    getCart().then(function (cart) {
      var present = {};
      (cart.items || []).forEach(function (i) {
        var p = i.properties || {};
        if (!p[PARENT_PROP]) present[String(i.variant_id || i.id)] = true;
      });
      var orphans = (cart.items || []).filter(function (i) {
        var p = i.properties || {};
        return p[PARENT_PROP] && !present[String(p[PARENT_PROP])];
      }).map(function (i) { return i.key; });

      if (!orphans.length) { reconciling = false; return; }

      log('removing orphaned wraps', orphans);
      var chain = Promise.resolve();
      orphans.forEach(function (key) {
        chain = chain.then(function () {
          return fetch(root() + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
        });
      });
      chain.then(function () { reconciling = false; refreshUI(false); })
           .catch(function () { reconciling = false; });
    }).catch(function () { reconciling = false; });
  }

  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, 250);
  }

  ['cart:change', 'cart:updated', 'cart:update', 'line-item:change', 'variant:add',
   'shopify:cart:lines-update', 'cart:lines-update', 'product:added'].forEach(function (name) {
    document.addEventListener(name, scheduleReconcile);
  });

  function observeCart() {
    var target = document.querySelector('cart-drawer-component')
      || document.querySelector('#cart-drawer, #CartDrawer, [id*="cart" i]')
      || document.body;
    try {
      new MutationObserver(scheduleReconcile).observe(target, { childList: true, subtree: true });
      log('observing cart node:', target && (target.id || target.tagName));
    } catch (e) {}
  }

  function init() {
    detachNoteFromForm();
    observeCart();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  log('initialised (no fetch override)');
})();